import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Micronutrients: The Hidden Heroes',
    content: [
      '<strong>Goal:</strong> To understand the vital role of vitamins and minerals in your health, and how to get them from Filipino foods.'
    ],
    additionalSection: {
      title: 'Why Micronutrients Matter',
      content: [
        '<strong>Core Concept:</strong> Micronutrients are essential nutrients needed in small amounts that play crucial roles in your health.',
        '<strong>Impact:</strong> They support immune function, energy production, bone health, and countless other vital processes.',
        '<strong>Local Context:</strong> Filipino cuisine is rich in various micronutrients when we include diverse, colorful foods.',
        '<br/>',
        '<strong>Key Message:</strong> "Small nutrients, big impact."'
      ]
    },
    additionalSection2: {
      title: 'Learning Objectives',
      content: [
        '<strong>1. Vitamin Knowledge:</strong> Identify key vitamins and their food sources.',
        '<strong>2. Mineral Awareness:</strong> Understand essential minerals for health.',
        '<strong>3. Local Sources:</strong> Recognize rich sources in Filipino cuisine.',
        '<strong>4. Daily Needs:</strong> Learn how to meet daily requirements through food.'
      ]
    }
  },
  {
    title: 'Essential Vitamins in Filipino Foods',
    content: [
      'Let\'s explore key vitamins and their local sources:',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header">Vitamin</div>
          <div class="table-header">Role in Health</div>
          <div class="table-header">Local Food Sources</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Vitamin A</div>
          <div class="table-cell means-cell">Vision, immune function</div>
          <div class="table-cell matters-cell">Malunggay, carrots, eggs</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Vitamin C</div>
          <div class="table-cell means-cell">Immunity, skin health</div>
          <div class="table-cell matters-cell">Calamansi, guava, papaya</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">B Vitamins</div>
          <div class="table-cell means-cell">Energy, brain function</div>
          <div class="table-cell matters-cell">Rice, bangus, kamote tops</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Activity: Vitamin Hunt</strong>',
      'In your kitchen, find foods rich in:',
      '• Vitamin A (orange/green vegetables)',
      '• Vitamin C (citrus fruits)',
      '• B Vitamins (whole grains, lean meats)'
    ]
  },
  {
    title: 'Important Minerals for Health',
    content: [
      'Understanding key minerals and where to find them:',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header">Mineral</div>
          <div class="table-header">Health Benefits</div>
          <div class="table-header">Filipino Sources</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Iron</div>
          <div class="table-cell means-cell">Blood health, energy</div>
          <div class="table-cell matters-cell">Malunggay, dried fish, liver</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Calcium</div>
          <div class="table-cell means-cell">Bone and teeth health</div>
          <div class="table-cell matters-cell">Small fish, gata, tahong</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Zinc</div>
          <div class="table-cell means-cell">Immune system, healing</div>
          <div class="table-cell matters-cell">Seafood, nuts, seeds</div>
        </div>
      </div>`,
      'Remember: Minerals work together with vitamins for optimal health.'
    ]
  },
  {
    title: 'Building a Micronutrient-Rich Diet',
    content: [
      'Practical tips for increasing your micronutrient intake:',
      '• <strong>Eat the Rainbow:</strong> Different colors mean different nutrients',
      '• <strong>Mix Your Greens:</strong> Combine various leafy vegetables',
      '• <strong>Include Local Superfoods:</strong> Malunggay, kamote tops, guyabano',
      '<br/>',
      '<strong>Weekly Challenge:</strong> Try to eat at least one new vitamin or mineral-rich food each day this week.'
    ],
    illustrationText: 'Nutrient Diversity'
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      "You've completed the module content! Now, let's see how well you understand micronutrients and their importance in Filipino foods."
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your understanding of micronutrients and their food sources.',
        'The quiz will help you review key concepts about vitamins, minerals, and their roles in maintaining good health.'
      ]
    }
  }
];

const NutritionalModule3Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('nutritionalM3');
  const totalSteps = moduleContent.length;

  // Ensure currentStep is within bounds
  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/nutritional-module-3/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/nutritional-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  // Error boundary for undefined module
  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div 
          className="module-left nutritional-panel"
        >
          <div className="module-header">
            <h1>Module 3</h1>
            <p className="module-subtitle">Eat Smart – More of the Good, Less of the Risky</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn to identify nutritious foods to increase and which ones to limit, making smarter choices for better health.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content"> 
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title"> 
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            
            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/nutritional-module-3/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalModule3Page;

